import * as _ from 'lodash';
import * as toastr from 'toastr';

import * as UserManagement from '../../';
import * as Common from '../../../../Common/modules';
import * as Models from '../../../../Models';
import * as Services from '../../../../Services';

export interface IUserSearchCtrlScope extends ng.IScope {
  userSearchCtrl: UserSearchCtrl;
}

export class UserSearchCtrl {
  public static $inject = [
    "$scope",
    "$state",
    "UserRetrievalService",
    "PartnerRetrievalService",
    "GroupRetrievalService",
    "UserService",
  ];
  public userNameOrEmail: string;
  public isCreatingUser = false;
  public suspendWorkIssueNotificationSlider =
    new UserManagement.models.suspendWorkIssueNotificationSlider.SuspendWorkIssueNotificationSlider();
  public createUserRequest: UserManagement.models.CreateUserRequest.ICreateUserRequest;
  public windowsUserNameUiMask: string;
  public userSearchRequest: UserManagement.models.UserSearchRequest.IUserSearchRequest;
  public possiblePartners: Models.PartnerModel.IPartnerModel[];
  public possibleGroups: Models.GroupModel.IGroupModel[];
  public currentUser: Models.UserModel.UserModel;
  public includeDisabledUsers: boolean = false;

  public resultsHeaders: Common.IgsGrid.Igs.Common.IIgsGridHeader[] = [
    { name: "Username", property: "username" },
    { name: "Email Address", property: "emailAddress" },
    { name: "Name", property: "displayName" },
    { name: "Agent Code", property: "agentCode" },
    {
      name: "Enabled",
      property: "isApproved",
      propertyConverter: (value: any) => {
        return value === true ? "Yes" : "No";
      },
    },
    {
      name: "",
      blockSort: true,
      action: {
        name: "View",
        css: "btn btn-default cyan-btn",
        click: (value: any) => {
          this.$state.go("main.usermanagement.edituser", { id: value.userId });
        },
      },
    },
  ];
  private showLogging: boolean = false;
  private roleList: Models.Auth.RoleList.RoleList = new Models.Auth.RoleList.RoleList();
  private users: UserManagement.models.UserSearchResult.IUserSearchResult[];

  constructor(
    $scope: IUserSearchCtrlScope,
    private $state: angular.ui.IStateService,
    private userRetrievalService: UserManagement.services.UserRetrievalService.IUserRetrievalService,
    private partnerRetrievalService: UserManagement.services.PartnerRetrievalService.IPartnerRetrievalService,
    private groupRetrievalService: UserManagement.services.GroupRetrievalService.IGroupRetrievalService,
    private userService: Services.UserService.IUserService,
  ) {
    $scope.userSearchCtrl = this;

    this.userService.getCurrentUser().then((data) => (this.currentUser = data));

    // only user with manage user can do searches
    this.userService.isUserInRole(this.roleList.manageUsers).then((response) => {
      if (response === false) {
        this.$state.go("main.customersearchv2");
      }
    });

    this.getAndSetCreateUserRequest();
    this.getAndSetPartners();
  }

  public getGroups(): ng.IPromise<void> {
    return this.groupRetrievalService
      .getGroupsInPartner(this.createUserRequest.partner.code)
      .then((groups: Models.GroupModel.IGroupModel[]) => {
        this.possibleGroups = _.sortBy(groups, "name");
        this.createUserRequest.groups = new Array<Models.GroupModel.IGroupModel>();
      });
  }

  public getButtonClass = (group: Models.GroupModel.IGroupModel) => {
    return this.groupIsSelected(group) ? "cyan-btn" : "";
  };

  public groupIsSelected = (group: Models.GroupModel.IGroupModel) => {
    return (
      this.createUserRequest &&
      _.some(this.createUserRequest.groups, (g: Models.GroupModel.IGroupModel) => g.groupId === group.groupId)
    );
  };

  public selectOrUnselectGroup = (group: Models.GroupModel.IGroupModel) => {
    if (this.showLogging) {
      console.log("group button clicked...");
    }
    if (this.groupIsSelected(group)) {
      this.createUserRequest.groups = _.filter(
        this.createUserRequest.groups,
        (g: Models.GroupModel.IGroupModel) => g.groupId !== group.groupId,
      );
    } else {
      this.createUserRequest.groups.push(group);
    }
  };

  public toggleSuspendWorkIssueNotification = (): void => {
    if (this.suspendWorkIssueNotificationSlider.enabled) {
      this.suspendWorkIssueNotificationSlider.enabled = false;
      this.createUserRequest.suspendWorkIssueNotification = true;
    } else {
      this.suspendWorkIssueNotificationSlider.enabled = true;
      this.createUserRequest.suspendWorkIssueNotification = false;
    }
  };

  public searchUsers = () => {
    if (this.showLogging) {
      console.log("searching for users...");
    }
    this.userSearchRequest = new UserManagement.models.UserSearchRequest.UserSearchRequest(
      this.userNameOrEmail || "",
      this.includeDisabledUsers,
    );
    this.isCreatingUser = false;
    this.userRetrievalService
      .searchUsers(this.userSearchRequest)
      .then((users: UserManagement.models.UserSearchResult.IUserSearchResult[]) => {
        this.users = _.sortBy(users, (user) => user.username.toLowerCase());
      })
      .catch(() => {
        toastr.error("There was an issue searching for users. Try again later.", "Error");
      });
  };

  public createUser = () => {
    if (this.showLogging) {
      console.log("creating new user...");
    }
    if (
      this.createUserRequest.password !== this.createUserRequest.password2 &&
      this.createUserRequest.randomizePassword === false
    ) {
      toastr.error("The Passwords do not match.", "Error");
      return;
    }
    if (!this.createUserRequest.email) {
      toastr.error("Please enter a valid email address", "Error");
      return;
    }

    if (!this.createUserRequest.agentCode && !this.createUserRequest.windowsUserName) {
      if (this.possiblePartners.length === 1) {
        toastr.error("Please provide an Agent Code. Tooltips are provided for more details", "Not Enough Data");
      } else {
        toastr.error(
          "Please provide a Windows Username and / or Agent Code. Tooltips are provided for more details",
          "Not Enough Data",
        );
      }
      return;
    }

    if (this.createUserRequest.windowsUserName) {
      this.createUserRequest.windowsUserName = this.createUserRequest.windowsUserName.trim();
    }

    this.userRetrievalService.createUser(this.createUserRequest).then((createUserResponse: any) => {
      if (createUserResponse.successful) {
        toastr.success("User has been successfully created!", "Success");
        this.getAndSetCreateUserRequest();
        this.getAndSetPartners();
      } else {
        if (createUserResponse.message !== "Error") {
          toastr.error(createUserResponse.message, "Error");
        } else {
          toastr.error("There was an issue creating the user. Try again later.", "Error");
        }
      }
    });
  };

  public onPartnerChanged() {
    this.getGroups();
  }

  public displayWindowsUsername() {
    return this.currentUser.partner.code === "IGS" || this.currentUser.partner.code === "IGSSALES";
  }

  // gets the possible partners for the current user
  private getAndSetPartners(): ng.IPromise<void> {
    if (this.showLogging) {
      console.log("getting partners...");
    }

    return this.partnerRetrievalService
      .getPartnersForUserManagement()
      .then((partners: Models.PartnerModel.IPartnerModel[]) => {
        this.possiblePartners = _.sortBy(partners, "name");

        if (this.possiblePartners.length === 1) {
          this.createUserRequest.partner = this.possiblePartners[0];
          this.onPartnerChanged();
        }
      });
  }

  private getAndSetCreateUserRequest = () => {
    if (this.showLogging) {
      console.log("setting new user form...");
    }
    this.isCreatingUser = false;
    this.createUserRequest = new UserManagement.models.CreateUserRequest.CreateUserRequest();
    this.createUserRequest.userId = 0;
    this.createUserRequest.enabled = true;
    this.createUserRequest.randomizePassword = true;
    this.createUiMask();
    this.suspendWorkIssueNotificationSlider =
      new UserManagement.models.suspendWorkIssueNotificationSlider.SuspendWorkIssueNotificationSlider();
  };

  private createUiMask = () => {
    this.windowsUserNameUiMask = "A?A?A?A?A?A?A?A?A?A?A?A?A?A?A?A?";
    this.windowsUserNameUiMask = _.repeat(this.windowsUserNameUiMask, 32);
  };

  public toggleIncludeDisabledUserSlider = (): void => {
    if (this.includeDisabledUsers) {
      this.includeDisabledUsers = false;
    } else {
      this.includeDisabledUsers = true;
    }
  };
}
